import { captureMessage } from '@sentry/react';
import { TFunction } from 'i18next';

import {
  ColorOption,
  formatAsNumber,
  TooltipFormatter,
} from '../generators/base';

export interface TooltipEntry {
  name: string;
  value: string | number | undefined;
  colorOption?: ColorOption;
}

const isFormatList = (
  params:
    | echarts.EChartOption.Tooltip.Format
    | echarts.EChartOption.Tooltip.Format[]
): params is echarts.EChartOption.Tooltip.Format[] => {
  return Array.isArray(params);
};

const priceTooltipFormatter =
  (t: TFunction) =>
  (formatObject: echarts.EChartOption.Tooltip.Format): TooltipEntry[] => {
    return ['open', 'high', 'low', 'close'].map(field => ({
      name: t(field),
      value: formatAsNumber(formatObject.data[field]),
      colorOption: { keyColor: 'grey' },
    }));
  };

const getMarkerStyleByDevice = (
  isMobile: boolean,
  entryColor: string,
  formatColor: string
) => {
  const markerDiameter = isMobile ? '6px' : '10px';
  const markerMarginRight = isMobile ? '2px' : '4px';
  return `<span style="display:inline-block;margin-right:${markerMarginRight};border-radius:${markerDiameter};width:${markerDiameter};height:${markerDiameter};background-color:${
    entryColor || formatColor
  };"></span>`;
};

const getTooltipPosition = (isMobile: boolean) => {
  return function (pos, _params, _el, _elRect, size) {
    const { contentSize, viewSize } = size;
    const obj = {};
    if (isMobile) {
      // behavior:
      // horizontally, the tooltip shows the opposite side of the cursor (1/3 from the left as mid point);
      // vertically, the tooltip shows the same side of the cursor;
      // the tooltip is always positioned in one of the four corners.
      // reference: https://echarts.apache.org/zh/option.html#tooltip.position
      obj[['top', 'bottom'][+(pos[1] > viewSize[1] / 2)]] = 30;
      obj['left'] = +(pos[0] > viewSize[0] / 3) ? 5 : undefined;
      obj['right'] = +(pos[0] < viewSize[0] / 3) ? 40 : undefined;
    } else {
      const margin = 10;
      const onRightSide = contentSize[0] + margin > pos[0];
      const onBottomSide = contentSize[1] + margin > pos[1];

      if (onRightSide) {
        obj['left'] = pos[0] + margin;
      } else {
        obj['right'] = viewSize[0] - pos[0] + margin;
      }

      if (onBottomSide) {
        obj['top'] = pos[1] + margin;
      } else {
        obj['bottom'] = viewSize[1] - pos[1] + margin;
      }
    }
    return obj;
  };
};

const getTooltipFormatter = (
  tooltipFormatterBySeriesName: Record<string, TooltipFormatter>,
  t: TFunction,
  isMobile
) => {
  return params => {
    const lines: string[] = [];
    const formats = isFormatList(params) ? params : [params];
    for (const format of formats) {
      const seriesName = format.seriesName;
      if (
        !tooltipFormatterBySeriesName[seriesName] &&
        seriesName !== 'Prices'
      ) {
        const seriesNames = Object.keys(tooltipFormatterBySeriesName);
        console.error(
          `Invalid series name ${seriesName}. Expected serie names: ${seriesNames}`
        );
        captureMessage('Invalid series name ' + seriesName);
        continue;
      }
      const formatterForSeries =
        tooltipFormatterBySeriesName[seriesName] || priceTooltipFormatter(t);
      for (const entry of formatterForSeries(format)) {
        if (entry.value === undefined) {
          continue;
        }
        const marker = getMarkerStyleByDevice(
          isMobile,
          entry.colorOption.keyColor,
          format.color
        );
        lines.push(
          `
          <div style="display: flex; justify-content: space-between;${
            isMobile ? ' font-size: 8px' : ''
          }; line-height: 1.5;">
            <div style="padding-right: 2rem;">${marker} ${entry.name}:</div>
            <div style="color: ${entry.colorOption.keyColor}"><strong>${
              entry.value
            }</strong></div>
          </div>`
        );
      }
    }
    return lines.join('');
  };
};

export const makeTooltip = (
  tooltipFormatterBySeriesName: Record<string, TooltipFormatter>,
  t: TFunction,
  isMobile: boolean
): echarts.EChartOption.Tooltip => {
  return {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
    },
    borderWidth: 1,
    borderColor: '#ccc',
    padding: isMobile ? 5 : 10,
    textStyle: {
      color: '#000',
      fontSize: 12,
    },
    position: getTooltipPosition(isMobile),
    formatter: getTooltipFormatter(tooltipFormatterBySeriesName, t, isMobile),
    backgroundColor: '#ffffffd0',
  };
};
